import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  styled,
  tooltipClasses,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { useAnalytics } from 'use-analytics';
import { ReactComponent as PulseInfoIcon } from '../../../src/assets/pulse-info-icon.svg';
import {
  AutomatedInsightSections,
  AutomatedInsightQuantToChatMenu,
  AutomatedInsightSessionType,
} from '../../modules/automated-insights/constants';
import { TrackEvent } from '../analytics';
import Carousel from '../reports/components/Carousel';
import PromptsSelect from './PromptsSelect';

const SUGGESTIONS = [
  'For exploratory research, open-ended questions tend to be the most effective.',
  'For hypothesis testing, be clear and specific, especially regarding the output that you are seeking.',
  'Avoid using words like “you”; instead use “respondents”, “the audience”, or “users.”',
  'If the question is complex, it is best to break it down into two questions.',
];

const PROMPTS = {
  TopRated: [
    'Recommend types of images that enhance the message of trust and expertise for our services page.',
    "What imagery would best represent our brand's values on the homepage?",
    "Evaluate this page's font size and color scheme for accessibility standards.",
    'Suggest changes to improve navigation accessibility for screen reader users.',
    'Apply Jakob Nielsen’s 10 Usability Heuristics and identify violations on our checkout page.',
    'Which of the 10 Usability Heuristics should we focus on improving first for a better user experience?',
    'Map the optimal user journey for a new visitor versus a returning customer.',
    'How does the user journey differ between our two main personas on the landing page?',
    "What's the user sentiment towards the current hero image on our main product page?",
    'How does changing the hero image to a more action-oriented picture affect user perception?',
    'List innovative A/B testing ideas tailored to our target personas.',
    'Propose A/B test ideas for the call-to-action buttons on our page.',
    'Identify what visitors expect to find on this page and how we can align with those expectations.',
    "What content do users expect to see on our 'About Us' page?",
    'Do users find the current FAQ section meets their expectations for clarity and comprehensiveness?',
    'Propose more engaging CTA wording to increase user action on this page.',
    'Offer alternative headlines for this page that might increase click-through rates.',
    'Create targeted questions to better understand user experience post-navigation.',
    'What custom questions should we include in our post-purchase survey to gauge customer satisfaction?',
    'What are the top 3 FAQs we should include on our product detail pages?',
    'Identify missing FAQs that could address common user concerns about our product.',
    'What single change would significantly enhance the impact of this page?',
    'Prioritize the redesign elements that would most enhance user engagement on our landing page.',
  ],
  [AutomatedInsightSections.KeyFindings]: [
    'How can I address the top concerns highlighted by the audience?',
    'Which website elements are performing well, and how can we enhance them further?',
    'Summarize the top concerns the audience has with our website.',
  ],
  [AutomatedInsightSections.SentimentMap]: [
    'Identify the main dislikes users have and explain how we can improve these areas.',
    'Which content creates the strongest positive feelings, and how can we replicate this success across the site?',
    'Highlight areas of confusion and provide suggestions on how to clarify them.',
  ],
  [AutomatedInsightSections.PersonaIntent]: [
    'What are the main reasons users visit our website?',
    'How does the intent vary between different personas?',
    'What can we do to better align our website with the users’ intents?',
  ],
  [AutomatedInsightSections.RecommendedExperiments]: [
    'What are some quick and effective changes we can make to improve navigation?',
    'What strategic changes would you recommend for long-term improvement?',
    'Propose A/B test ideas to evaluate these recommendations.',
  ],
  EQSIntuitive: [
    'What features or elements could be simplified to enhance overall user intuitiveness?',
    'Identify any part of our site that may cause confusion. How can we make it clearer?',
    'Recommend improvements to make navigating our content more straightforward for all users.',
  ],
  EQSTrusted: [
    "How can we boost our website's credibility and trust among users?",
    'Review our website for trust indicators. What can be enhanced to ensure a secure feeling?',
    'Highlight areas for improvement on displaying testimonials or endorsements to build trust.',
  ],
  EQSValuable: [
    'What aspects of our offering should we emphasize to convey greater value to users?',
    'Propose methods to better showcase the real-life benefits of our offerings to users.',
    'Assess how effectively our product or service benefits are communicated. Suggest enhancements.',
  ],
};

const SECTIONS = {
  [AutomatedInsightSessionType.Page]: [
    'TopRated',
    AutomatedInsightSections.KeyFindings,
    AutomatedInsightSections.SentimentMap,
    AutomatedInsightSections.PersonaIntent,
    AutomatedInsightSections.RecommendedExperiments,
    'EQSIntuitive',
    'EQSTrusted',
    'EQSValuable',
  ],
  [AutomatedInsightSessionType.Experience]: [
    'TopRated',
    AutomatedInsightSections.KeyFindings,
    AutomatedInsightSections.SentimentMap,
    AutomatedInsightSections.PersonaIntent,
    AutomatedInsightSections.RecommendedExperiments,
  ],
};

const TipsTooltip = styled(({ className, maxWidth, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme, maxWidth }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#FCFCFC',
    color: theme.palette.text.primary,
    borderRadius: '24px',
    border: '1px solid #E0E0E0',
    maxWidth: maxWidth,
  },
}));

const Tips = () => {
  return (
    <Box p={2}>
      <Typography component="span" sx={{ fontSize: 12 }}>
        Tips on using the chat feature in WEVO Pulse:
      </Typography>
      <Box>
        <ul style={{ marginBottom: 0, paddingInlineStart: '24px' }}>
          {SUGGESTIONS.map((item, index) => (
            <li key={index}>
              <Typography fontSize="12px">{item}</Typography>
            </li>
          ))}
        </ul>
      </Box>
    </Box>
  );
};

const QuestionExample = ({ question, isSelected }) => (
  <Box
    sx={{
      minHeight: '70px',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      backgroundColor: isSelected ? 'rgba(43, 77, 101, 0.11)' : '#FCFCFC',
      borderRadius: '16px',
      border: '0.5px solid #E0E0E0',
      paddingX: 2,
      paddingY: 1,
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: 'rgba(43, 77, 101, 0.11)',
      },
    }}>
    <Typography fontSize="11px">{question}</Typography>
  </Box>
);

const ChatInfo = ({
  sessionId,
  currentInput,
  onQuestionSelection,
  showCarousel,
  sessionType,
  quantCategory = null,
}) => {
  const [expandChatInfo, setExpandChatInfo] = useState(true);
  const [showToggleTooltip, setShowToggleTooltip] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState('TopRated');

  useEffect(() => {
    setSelectedMenuItem(quantCategory ? AutomatedInsightQuantToChatMenu[quantCategory] : 'TopRated');
  }, [quantCategory]);

  const { track } = useAnalytics();
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isMdAndUp = useMediaQuery(theme.breakpoints.up('md'));
  const isLgAndUp = useMediaQuery(theme.breakpoints.up('lg'));
  const isXlAndUp = useMediaQuery(theme.breakpoints.up('xl'));

  const { width: containerWidth, ref: containerRef } = useResizeDetector();

  const handleChatInfoToggled = () => {
    setExpandChatInfo(!expandChatInfo);
    setShowToggleTooltip(false);
  };

  const handleQuestionClick = ({ item }) => {
    track(TrackEvent.SELECTED_SUGGESTED_PULSE_CHAT_QUESTION, { sessionId, question: item });
    onQuestionSelection(item);
  };

  const getNumOfItemsToDisplay = () => {
    if (isXlAndUp) {
      return 3.1;
    } else if (isLgAndUp) {
      return 2.5;
    } else if (isSm || isMdAndUp) {
      return 2.2;
    } else {
      return 1.2;
    }
  };

  const changeSection = (ev) => {
    const newSelectedMenuItem = ev.target.value;

    if (newSelectedMenuItem !== selectedMenuItem) {
      setSelectedMenuItem(newSelectedMenuItem);
      track(TrackEvent.SELECTED_PULSE_CHAT_SUGGESTED_PROMPTS_MENU_ITEM, {
        suggestedPromptsMenuItem: newSelectedMenuItem,
      });
    }
  };

  return (
    <Grid container p={2} spacing={1}>
      <Grid item container justifyContent="space-between" alignItems="center" ref={containerRef}>
        <Box>
          <Grid container>
            <TipsTooltip placement="right" maxWidth={containerWidth * 0.9} title={<Tips />}>
              <IconButton aria-label="tips" size="small">
                <PulseInfoIcon />
              </IconButton>
            </TipsTooltip>
            <Box sx={{ marginLeft: 0.7 }}>
              <PromptsSelect
                selectedMenuItem={selectedMenuItem}
                sections={SECTIONS?.[sessionType] || SECTIONS[AutomatedInsightSessionType.Page]}
                onMenuItemChange={changeSection}
              />
            </Box>
          </Grid>
        </Box>
        <Box onClick={handleChatInfoToggled} sx={{ cursor: 'pointer' }}>
          {!expandChatInfo && (
            <Typography variant="caption" pr={2}>
              View Suggested Prompts
            </Typography>
          )}
          <Tooltip
            open={showToggleTooltip}
            title={expandChatInfo ? 'Hide suggested prompts' : ''}
            placement="top-end"
            disableFocusListener={true}
            onOpen={() => setShowToggleTooltip(true)}
            onClose={() => setShowToggleTooltip(false)}>
            <span>
              <IconButton sx={{ width: 14, height: 14 }}>
                {expandChatInfo ? <ExpandMoreIcon /> : <ExpandLessIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Grid>
      <Grid
        item
        container
        spacing={1}
        alignItems="stretch"
        sx={{ display: expandChatInfo ? 'flex' : 'none', borderRadius: '20px' }}>
        {showCarousel ? (
          <Carousel
            items={PROMPTS[selectedMenuItem]}
            itemComponent={({ index, style, item }) => {
              return <QuestionExample question={item} isSelected={item === currentInput} />;
            }}
            numItemsDisplayed={getNumOfItemsToDisplay()}
            itemHeight={isLgAndUp ? 80 : 90}
            backgroundColor={'#FFFFFF'}
            arrowHeight={16}
            onSelection={handleQuestionClick}
          />
        ) : (
          PROMPTS[selectedMenuItem].map((question, index) => (
            <Grid item xs={6} key={index} onClick={() => handleQuestionClick(question)}>
              <QuestionExample question={question} isSelected={question === currentInput} />
            </Grid>
          ))
        )}
      </Grid>
    </Grid>
  );
};

export default ChatInfo;
